import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowClockwise, Eye, ListBullets, Spinner, Table as TableIcon } from '@phosphor-icons/react'
import { Button, Icon } from '@shopflo/ui'
import { useLocale } from 'src/lib/hooks/useLocal'

const LOADING_SENTENCES = [
  'segment_creation.count_loading_1',
  'segment_creation.count_loading_2',
  'segment_creation.count_loading_3'
]

const AnimatedEllipsis = () => (
  <span className='inline-flex'>
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          delay: i * 0.2
        }}
      >
        .
      </motion.span>
    ))}
  </span>
)

export type JobLoadState = {
  isLoading: boolean
  stagePercent?: number
}

export type EngagementNudgeBodyProps = {
  hasConditions: boolean
  showCount: boolean
  countJob: JobLoadState
  previewJob: JobLoadState
  tickerIndex: number
  audienceCount: number | null
  fetchAudienceCount: () => void
  onPreviewDataClick: () => void
  dimensionIds: string[]
}

const nudgeBlurIn = { opacity: 0, filter: 'blur(8px)' as const }
const nudgeBlurOut = { opacity: 0, filter: 'blur(8px)' as const }
const nudgeBlurSettled = { opacity: 1, filter: 'blur(0px)' as const }
const nudgePromptLineEnterY = -10
const nudgePromptLineExitY = 10
const nudgePromptLineTransition = { duration: 0.28, ease: 'easeOut' as const }

const nudgePanelTransition = { duration: 0.34, ease: 'easeOut' as const }
const nudgePanelMotionClass =
  'will-change-[transform,filter,opacity] flex w-full min-w-0 max-w-full flex-col items-center justify-center text-center'
const nudgePresenceMinHeightClass = 'min-h-[288px]'
const nudgeCopyColumnClass = 'w-full max-w-xs px-2'
const nudgePromptRowClass = `relative flex min-h-12 items-center justify-center ${nudgeCopyColumnClass}`

export const EngagementNudgeBody: React.FC<EngagementNudgeBodyProps> = ({
  hasConditions,
  showCount,
  countJob,
  previewJob,
  tickerIndex,
  audienceCount,
  fetchAudienceCount,
  onPreviewDataClick,
  dimensionIds
}) => {
  const { t } = useLocale()

  return (
    <div
      className={`relative flex w-full shrink-0 flex-col items-center justify-center overflow-visible ${nudgePresenceMinHeightClass}`}
    >
      <AnimatePresence mode='popLayout' initial={false}>
        {!hasConditions ? (
          <motion.div
            key='nudge-early-info'
            layout={false}
            className={nudgePanelMotionClass}
            initial={{ opacity: 0, y: 14, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -12, filter: 'blur(10px)' }}
            transition={nudgePanelTransition}
          >
            <div
              className={`flex flex-col items-center gap-3 py-2 ${nudgeCopyColumnClass}`}
            >
              <Icon
                IconComponent={ListBullets}
                size='lg'
                iconProps={{ weight: 'duotone' }}
                className='text-zinc-400'
              />
              <p className='w-full text-wrap py-1 text-center text-text-xs-medium text-zinc-500'>
                {t('segment_creation.preview_add_conditions_message')}
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key='nudge-with-conditions'
            layout={false}
            className={`${nudgePanelMotionClass} gap-1 overflow-visible ${previewJob.isLoading ? 'opacity-80' : ''}`}
            initial={{ opacity: 0, y: 14, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -12, filter: 'blur(10px)' }}
            transition={nudgePanelTransition}
          >
            <div className='flex min-h-[100px] w-full flex-col items-center justify-center gap-1'>
              <Icon
                IconComponent={TableIcon}
                size='lg'
                iconProps={{ weight: 'duotone' }}
                className='text-zinc-400'
              />
              {showCount ? (
                <div className={nudgePromptRowClass}>
                  <AnimatePresence mode='popLayout' initial={false}>
                    {countJob.isLoading ? (
                      <motion.div
                        key='loading-count'
                        layout={false}
                        initial={{ ...nudgeBlurIn, y: nudgePromptLineEnterY }}
                        animate={{ ...nudgeBlurSettled, y: 0 }}
                        exit={{ ...nudgeBlurOut, y: nudgePromptLineExitY }}
                        transition={nudgePromptLineTransition}
                        className='relative min-h-12 w-full overflow-visible'
                      >
                        <AnimatePresence mode='popLayout'>
                          <motion.div
                            key={tickerIndex}
                            layout={false}
                            initial={{ y: nudgePromptLineEnterY, opacity: 0, filter: 'blur(8px)' }}
                            animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
                            exit={{ y: nudgePromptLineExitY, opacity: 0, filter: 'blur(8px)' }}
                            transition={{ duration: 0.3 }}
                            className='absolute inset-0 flex items-center justify-center'
                          >
                            <p className='w-full text-wrap text-center text-text-xs-medium text-zinc-500'>
                              {t(LOADING_SENTENCES[tickerIndex])}
                              <AnimatedEllipsis />
                            </p>
                          </motion.div>
                        </AnimatePresence>
                      </motion.div>
                    ) : audienceCount !== null ? (
                      <motion.div
                        key='audience-count'
                        layout={false}
                        initial={{ ...nudgeBlurIn, y: nudgePromptLineEnterY }}
                        animate={{ ...nudgeBlurSettled, y: 0 }}
                        exit={{ ...nudgeBlurOut, y: nudgePromptLineExitY }}
                        transition={nudgePromptLineTransition}
                        className='flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center'
                      >
                        <span className='text-text-sm-medium text-zinc-500'>
                          {t('segment_creation.found_label')}
                        </span>
                        <span className='text-text-md-semibold text-zinc-700'>
                          {audienceCount.toLocaleString()} {t('segment_creation.matching_results_label')}
                        </span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key='count-prompt'
                        layout={false}
                        initial={{ ...nudgeBlurIn, y: nudgePromptLineEnterY }}
                        animate={{ ...nudgeBlurSettled, y: 0 }}
                        exit={{ ...nudgeBlurOut, y: nudgePromptLineExitY }}
                        transition={nudgePromptLineTransition}
                        className='w-full text-wrap text-center text-text-xs-medium text-zinc-500'
                      >
                        {t('segment_creation.count_prompt')}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className={nudgeCopyColumnClass}>
                  <p className='w-full text-wrap text-center text-text-xs-medium text-zinc-500'>
                    {t('segment_creation.count_prompt')}
                  </p>
                </div>
              )}
            </div>

            <div className='flex w-full flex-col items-center justify-center gap-5'>
              <div className='h-px w-full bg-gradient-to-r from-zinc-500/0 via-zinc-500/20 to-zinc-500/0' />
              <div className='flex w-full flex-row items-center justify-center gap-1.5 px-2'>
                {showCount ? (
                  <Button
                    variant='dashed'
                    size='md'
                    onClick={fetchAudienceCount}
                    loading={countJob.isLoading}
                    loadingProgress={countJob.stagePercent}
                    disabled={countJob.isLoading || previewJob.isLoading}
                    // Button disabled default is opacity-40, which hides the fill. 70% keeps the progress bar visible while loading.
                    className={`w-36 bg-transparent${countJob.isLoading ? ' disabled:!opacity-70' : ''}`}
                  >
                    <span className='flex items-center justify-center gap-2'>
                      {countJob.isLoading ? (
                        <>
                          <Icon IconComponent={Spinner} size='sm' className='animate-spin' />
                          <span>{t('segment_creation.fetching_label')}</span>
                        </>
                      ) : (
                        <>
                          <Icon IconComponent={ArrowClockwise} size='sm' />
                          <span>{t('segment_creation.count_data')}</span>
                        </>
                      )}
                    </span>
                  </Button>
                ) : null}

                <Button
                  variant='dashed'
                  size='md'
                  onClick={onPreviewDataClick}
                  loading={previewJob.isLoading}
                  loadingProgress={previewJob.stagePercent}
                  disabled={countJob.isLoading || previewJob.isLoading || dimensionIds.length === 0}
                  // Button disabled default is opacity-40, which hides the fill. 70% keeps the progress bar visible while loading.
                  className={`w-36 bg-transparent${previewJob.isLoading ? ' disabled:!opacity-70' : ''}`}
                >
                  <span className='flex items-center justify-center gap-2'>
                    {previewJob.isLoading ? (
                      <>
                        <Icon IconComponent={Spinner} size='sm' className='animate-spin' />
                        <span>{t('segment_creation.fetching_label')}</span>
                      </>
                    ) : (
                      <>
                        <Icon IconComponent={Eye} size='sm' />
                        <span>Preview Data</span>
                      </>
                    )}
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
